import { useGetChannelOwnerships } from '@/api/channel'
import pagePath from '@/utils/pagePath'
import { isVerified } from '@/utils/verify'
import styled from '@emotion/styled'
import { Avatar, Button, Flex, Tag, Text, Title } from '@collable/ui'
import { useMemo } from 'react'
import { Link } from 'react-router'

type Page = 'video' | 'channel'

interface MyChannelsSidebarProps {
  page: Page
}

const createLink = (page: Page, email: string) => {
  if (page === 'video') {
    return pagePath.channelVideos(email)
  }
  return pagePath.channelDetail(email)
}

const TAG_TEXT = {
  REQUESTED: '승인 대기 중',
  REJECTED: '승인 실패',
} as const

const MyChannelsSidebar = ({ page }: MyChannelsSidebarProps) => {
  const { data: channelOwnerships } = useGetChannelOwnerships()

  const verifiedChannelOwnerships = channelOwnerships?.filter(
    channel => channel.status === 'VERIFIED' || channel.status === 'ACTIVATED',
  )

  const channelLists = useMemo(() => {
    if (page === 'video') return channelOwnerships
    return channelOwnerships?.filter(channel => !!channel.channelId)
  }, [page, channelOwnerships])

  return (
    <Container as="aside" direction="column" gap={32}>
      <Title size="subTitle1" fontWeight="bold" as="h2">
        내 채널
      </Title>
      <Flex as="ul" direction="column" gap={4}>
        {channelLists?.map(channel => {
          const verified = isVerified(channel.status)
          return (
            <li key={channel.channelId}>
              <Link to={createLink(page, channel.email)}>
                <Item alignItems="center" gap={12} isVerified={verified}>
                  <Avatar type="creator" size="m" src={channel.channelThumbnail} />
                  <Flex direction="column" gap={6}>
                    <Text size="body2" fontWeight="medium" color="labelNeutral">
                      {channel.channelName || channel.email}
                    </Text>
                    {!verified && (
                      <Tag
                        size="small"
                        type="solid"
                        color={channel.status === 'REQUESTED' ? 'warning' : 'danger'}
                      >
                        {TAG_TEXT[channel.status]}
                      </Tag>
                    )}
                  </Flex>
                </Item>
              </Link>
            </li>
          )
        })}
      </Flex>
      {!!verifiedChannelOwnerships?.length && (
        <Link to={pagePath.selectVerifyMethod}>
          <Button size="medium" kind="outline" color="neutral">
            다른 채널 등록하기
          </Button>
        </Link>
      )}
    </Container>
  )
}

export default MyChannelsSidebar

const Container = styled(Flex)`
  width: 320px;
  min-width: 320px;
  height: 100%;
  background-color: ${({ theme }) => theme.common.commonWhite};
  padding: 48px 0 48px 48px;

  a {
    margin-top: auto;
  }
`

const Item = styled(Flex)<{
  isVerified: boolean
}>`
  background-color: ${({ isVerified, theme }) =>
    isVerified ? theme.common.commonWhite : theme.fill.fillAssistive};
  border-radius: 16px;
  padding: 10px 12px;
`
